import { useCallback, useId, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { CheckState } from '@/storybook/components/FormCollection/Checkbox/Checkbox';
import type { TreeItemProps, TreeItemVariant } from './TreeItem.types';
import styles from './TreeItem.module.css';

function getRowAriaRoleAndChecked(showCheckbox: boolean, flatListCheckbox: boolean, checked: CheckState) {
  const useCheckboxRowRole = showCheckbox && !flatListCheckbox;
  const ariaRole = useCheckboxRowRole ? 'checkbox' : showCheckbox ? 'group' : 'option';
  const ariaChecked = useCheckboxRowRole
    ? checked === 'indeterminate'
      ? ('mixed' as const)
      : (checked as boolean)
    : undefined;
  return { ariaRole, ariaChecked };
}

function resolveVariantLayout(variant: TreeItemVariant, showCheckbox: boolean) {
  const leadingGutter = variant === 'tree' ? ('tree' as const) : ('none' as const);
  const flatListCheckbox = variant === 'flatList' && showCheckbox;
  return { leadingGutter, flatListCheckbox };
}

function getRowTabIndex(isDisabled: boolean, tabIndex: number | undefined, isFlatListCheckbox: boolean): number {
  if (isDisabled) return -1;
  if (tabIndex !== undefined) return tabIndex;
  return isFlatListCheckbox ? -1 : 0;
}

export type UseTreeItemRowStateParams = Pick<
  TreeItemProps,
  | 'variant'
  | 'hasChildren'
  | 'isExpanded'
  | 'onToggleExpand'
  | 'showCheckbox'
  | 'checked'
  | 'onCheck'
  | 'isSelected'
  | 'isDisabled'
  | 'tabIndex'
  | 'onClick'
  | 'className'
>;

export function useTreeItemRowState({
  variant = 'tree',
  hasChildren = false,
  isExpanded = false,
  onToggleExpand,
  showCheckbox = false,
  checked = false,
  onCheck,
  isSelected = false,
  isDisabled = false,
  tabIndex,
  onClick,
  className: classNameProp,
}: UseTreeItemRowStateParams) {
  const { leadingGutter, flatListCheckbox } = resolveVariantLayout(variant, showCheckbox);

  const labelRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const labelId = useId();

  const checkTruncation = useCallback(() => {
    const el = labelRef.current;
    if (!el) return;
    setIsTruncated(el.scrollHeight > el.clientHeight + 1);
  }, []);

  const handleBadgeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleCaretClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleExpand?.();
    },
    [onToggleExpand],
  );

  const handleItemClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDisabled) return;
      if (onClick) {
        onClick(e);
      } else if (showCheckbox) {
        onCheck?.();
      }
    },
    [isDisabled, onClick, onCheck, showCheckbox],
  );

  const handleItemActivateFromKeyboard = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isDisabled) return;
      if (onClick) {
        onClick(e);
      } else if (showCheckbox) {
        onCheck?.();
      }
    },
    [isDisabled, onClick, onCheck, showCheckbox],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isDisabled) return;
      if (e.key === 'ArrowRight' && hasChildren && !isExpanded) {
        e.preventDefault();
        onToggleExpand?.();
        return;
      }
      if (e.key === 'ArrowLeft' && hasChildren && isExpanded) {
        e.preventDefault();
        onToggleExpand?.();
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleItemActivateFromKeyboard(e);
      }
    },
    [isDisabled, hasChildren, isExpanded, onToggleExpand, handleItemActivateFromKeyboard],
  );

  const { ariaRole, ariaChecked } = useMemo(
    () => getRowAriaRoleAndChecked(showCheckbox, flatListCheckbox, checked),
    [showCheckbox, flatListCheckbox, checked],
  );

  const showCaretColumn = hasChildren || leadingGutter === 'tree';

  const rootClassName = cx(styles.treeItem, classNameProp, {
    [styles.selected]: isSelected,
    [styles.disabled]: isDisabled,
    [styles.treeItemMenu]: leadingGutter === 'none',
  });

  const caretButtonClassName = cx(styles.caretButton, { [styles.caretExpanded]: isExpanded });

  const rowTabIndex = getRowTabIndex(isDisabled, tabIndex, showCheckbox && flatListCheckbox);

  return {
    labelRef,
    isTruncated,
    labelId,
    checkTruncation,
    handleBadgeClick,
    handleCaretClick,
    handleItemClick,
    handleKeyDown,
    ariaRole,
    ariaChecked,
    showCaretColumn,
    rootClassName,
    caretButtonClassName,
    rowTabIndex,
  };
}
